import React, { useCallback, useEffect, useState } from "react";
import { FaTachometerAlt } from "react-icons/fa";
import { SectionCard } from "../SectionCard";
import { fetchOrgLimits } from "../api";
import { fmtN, describeLimits, SEVERITY_COLOR } from "../format";

const ACCENT = "#7c3aed";

function Meter({ label, count, limit }) {
  const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 1;
  const ratio = (count || 0) / safeLimit;
  const pct = Math.min(100, Math.max(0, ratio * 100));
  const sev = ratio >= 0.9 ? "critical" : ratio >= 0.7 ? "watch" : "ok";
  const color = SEVERITY_COLOR[count ? sev : "idle"];
  return (
    <div className="oo-meter">
      <div className="oo-meter-top">
        <span>{label}</span>
        <span className="oo-meter-count">
          {fmtN(count || 0)} / {Number.isFinite(limit) ? fmtN(limit) : "—"}
        </span>
      </div>
      <div className="oo-meter-track">
        <div className="oo-meter-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export default function LimitsSection({ organizationId, env, onMetrics }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [desc, setDesc] = useState(null);
  const [found, setFound] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { org } = await fetchOrgLimits(organizationId, env);
      if (!org) {
        setFound(false);
        setDesc(null);
        onMetrics?.({ limitSeverity: null });
        return;
      }
      setFound(true);
      const d = describeLimits(org);
      setDesc(d);
      onMetrics?.({ limitSeverity: d?.worstSev || "ok" });
    } catch (err) {
      setError(err.message || "Failed to load usage limits");
    } finally {
      setLoading(false);
    }
  }, [organizationId, env, onMetrics]);

  useEffect(() => {
    load();
  }, [load]);

  const chips = [];
  if (desc?.autoRun) chips.push({ label: "Auto-run", cls: "oo-chip--ok" });
  if (desc?.splitting) chips.push({ label: "Doc splitting", cls: "oo-chip" });
  if (desc?.hasOverride) chips.push({ label: "Custom limits", cls: "oo-chip" });
  if (desc?.isNewFi) chips.push({ label: "NewFi", cls: "oo-chip" });

  return (
    <SectionCard
      title="Usage Limits"
      subtitle="AI quotas and processing toggles"
      icon={<FaTachometerAlt />}
      accent={ACCENT}
      loading={loading && !desc && found}
      error={error}
      onRetry={load}
      size="third"
      link={{
        to: `/organization-limits?org=${encodeURIComponent(organizationId)}&env=${env}`,
        label: "Open Organization Limits",
      }}
    >
      {!found ? (
        <div className="oo-empty-hint">
          No usage-limit record found for this org. It may use system defaults
          with no recent activity.
        </div>
      ) : desc ? (
        <>
          <Meter label="Doc processing (OCR)" count={desc.ocr.count} limit={desc.ocr.limit} />
          <Meter label="PC auto-runs" count={desc.pc.count} limit={desc.pc.limit} />
          {chips.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
              {chips.map((c) => (
                <span key={c.label} className={`oo-chip ${c.cls}`}>
                  {c.label}
                </span>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </SectionCard>
  );
}
